import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { NEXT_STEP_KIND, type NextStepItem, type NextStepKind } from './buildNextSteps';

const ICON_BY_KIND: Record<NextStepKind, IconName> = {
  [NEXT_STEP_KIND.intakeReturned]: 'ClipboardList',
  [NEXT_STEP_KIND.intakeDraft]: 'ClipboardList',
  [NEXT_STEP_KIND.intakeNotStarted]: 'ClipboardList',
  [NEXT_STEP_KIND.exercisesDue]: 'Heart',
  [NEXT_STEP_KIND.unreadMessages]: 'MessageCircle',
  [NEXT_STEP_KIND.consentsNeedReacceptance]: 'ShieldCheck',
};

interface NextStepsCardProps {
  items: NextStepItem[];
  isLoading: boolean;
}

export function NextStepsCard({ items, isLoading }: NextStepsCardProps) {
  const { t } = useTranslation(['clients', 'intake']);
  const navigate = useNavigate();

  function resolveDisplayParams(item: NextStepItem): Record<string, string | number> {
    if (
      (item.kind === NEXT_STEP_KIND.intakeReturned || item.kind === NEXT_STEP_KIND.intakeDraft) &&
      item.params.petName === ''
    ) {
      return { ...item.params, petName: t('intake:portalHome.unnamedPet') };
    }
    return item.params;
  }

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="ClipboardCheck" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('clients:portal.nextSteps.title')}</h2>
      </div>

      {isLoading === true ? (
        <div className="py-6 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-2">
          <p className="text-sm font-semibold text-zooindigo">
            {t('clients:portal.nextSteps.emptyTitle')}
          </p>
          <p className="text-xs text-zoomuted mt-1">
            {t('clients:portal.nextSteps.emptyDescription')}
          </p>
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map(function renderItem(item) {
            const displayParams = resolveDisplayParams(item);
            return (
              <li
                key={item.kind + ':' + item.actionRoute}
                className="flex items-center justify-between gap-3 p-3 border border-zooborder rounded-xl"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
                    <Icon name={ICON_BY_KIND[item.kind]} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-zooindigo">
                      {t(item.titleKey, displayParams)}
                    </p>
                    <p className="text-xs text-zoomuted">{t(item.descriptionKey, displayParams)}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="chip"
                  className="shrink-0 min-h-[44px]"
                  onClick={function goToAction() {
                    navigate(item.actionRoute);
                  }}
                >
                  {t(item.actionLabelKey, displayParams)}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
